import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface MyWhatsAppInstance {
  id: string;
  user_id: string;
  evolution_instance_name: string;
  connection_status: 'disconnected' | 'connecting' | 'connected';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  server_id?: string | null;
  use_shared_session?: boolean;
}

export const useMyWhatsAppInstance = () => {
  const [instance, setInstance] = useState<MyWhatsAppInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMyInstance = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data, error: fetchError } = await supabase
        .from('whatsapp_providers')
        .select('*')
        .eq('user_id', user.id)
        .eq('provider_type', 'evolution')
        .eq('is_active', true)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      setInstance(data as MyWhatsAppInstance || null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar instância';
      setError(errorMessage);
      console.error('Error loading WhatsApp instance:', err);
    } finally {
      setLoading(false);
    }
  };

  const createOrConnect = async (
    instanceName: string,
    serverId?: string | null,
    useSharedSession?: boolean
  ) => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Verificar se já existe instância
      if (instance) {
        // Atualizar instância existente
        const { data, error: updateError } = await supabase
          .from('whatsapp_providers')
          .update({
            evolution_instance_name: instanceName,
            connection_status: useSharedSession ? 'connected' : 'connecting',
            server_id: serverId ?? instance.server_id,
            use_shared_session: useSharedSession ?? instance.use_shared_session,
          })
          .eq('id', instance.id)
          .select()
          .single();

        if (updateError) throw updateError;
        setInstance(data as MyWhatsAppInstance);
        return data;
      } else {
        // Criar nova instância - usar user_id (auth.users.id), trigger preenche app_user_id
        const { data, error: insertError } = await supabase
          .from('whatsapp_providers')
          .insert([{
            user_id: user.id,
            provider_type: 'evolution',
            evolution_instance_name: instanceName,
            is_active: true,
            server_id: serverId ?? null,
            use_shared_session: useSharedSession ?? false,
          }])
          .select()
          .single();

        if (insertError) throw insertError;
        setInstance(data as MyWhatsAppInstance);
        return data;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao criar/conectar instância';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateConnectionStatus = async (status: 'disconnected' | 'connecting' | 'connected') => {
    if (!instance) return;

    try {
      const { data, error: updateError } = await supabase
        .from('whatsapp_providers')
        .update({
          connection_status: status,
        })
        .eq('id', instance.id)
        .select()
        .single();

      if (updateError) throw updateError;
      setInstance(data as MyWhatsAppInstance);
    } catch (err) {
      console.error('Error updating connection status:', err);
    }
  };

  const disconnect = async () => {
    if (!instance) return;

    try {
      setLoading(true);

      const { data, error: updateError } = await supabase
        .from('whatsapp_providers')
        .update({
          connection_status: 'disconnected',
          is_active: false,
        })
        .eq('id', instance.id)
        .select()
        .single();

      if (updateError) throw updateError;

      setInstance(data as MyWhatsAppInstance);
      toast.success('WhatsApp desconectado com sucesso!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao desconectar';
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMyInstance();
  }, []);

  return {
    instance,
    loading,
    error,
    loadMyInstance,
    createOrConnect,
    updateConnectionStatus,
    disconnect,
  };
};
